import { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAnimation } from 'motion/react';
import Icon, { ICON_RIGHT, ICON_LEFT } from '~/components/icon/index.js';
import {
	Slider,
	MotionSlider,
	SingleItemWrapper,
	SlideItemWrapper,
	ArrowLeft,
	ArrowRight
} from './SliderComponents.js';
import UIContext from '../../ui/ui-context';
import { variants, swipeConfidenceThreshold, swipePower } from './utils.js';
import usePrevious from '~/hooks/use-previous.js';

const ITEM_SPACING = 24;

const WrappedSlider = ({
	children,
	onNext,
	onPrevious,
	currentSlide,
	displayArrows,
	arrowStyle,
	skipStep,
	itemSpacing,
	arrowsTopPosition
}) => {
	const { withinSidebar } = useContext(UIContext);
	const controls = useAnimation();
	const sliderRef = useRef();
	const [sliderWidth, setSliderWidth] = useState(0);
	const slideOffset = itemSpacing || ITEM_SPACING;
	const itemWidth = sliderWidth - slideOffset * 2;
	const previousChildrenLength = usePrevious(children?.length);

	// poke motion whenever something cool happens
	useEffect(() => {
		controls.start('next');
	}, [controls, currentSlide, sliderWidth]);

	// update sliderWidth whenever sliderRef changes or window is resized
	useEffect(() => {
		const handleResize = () => {
			if (sliderRef.current) {
				setSliderWidth(sliderRef.current.clientWidth);
			}
		};
		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [sliderRef]);

	// Reposition if number of children changes
	useEffect(() => {
		if (previousChildrenLength && previousChildrenLength !== children.length) {
			controls.start('next');
		}
	}, [children, previousChildrenLength, controls]);

	// create callbacks for motion
	const getHorizontalPositionForIndex = useCallback(
		index => (index - currentSlide) * itemWidth + slideOffset,
		[currentSlide, itemWidth, slideOffset]
	);

	// const getOpacityForIndex = useCallback(index => (currentSlide === index ? '1.0' : '0.4'), [currentSlide]);

	// Motion slider configuration constants
	const dragConfig = {
		drag: 'x',
		dragConstraints: { left: 0, right: 0 },
		dragElastic: 0.1, // Reduced from 0.2 for less sensitivity
		whileDrag: { cursor: 'grabbing' }
	};

	const handleDragEnd = (e, { offset, velocity }) => {
		// Only process swipes if we had significant movement
		if (Math.abs(offset.x) > 5) {
			const swipe = swipePower(offset.x, velocity.x);

			if (swipe < -swipeConfidenceThreshold) {
				onNext();
			} else if (swipe > swipeConfidenceThreshold) {
				onPrevious();
			}
		}
	};

	const itemTransition = {
		opacity: {
			type: 'tween',
			duration: 22
		},
		x: {
			type: 'spring',
			stiffness: 300,
			damping: 25
		}
	};

	const getItemStyle = () => (skipStep !== undefined ? { height: 'auto' } : {});

	const getCustomProps = index => ({
		distribute: getHorizontalPositionForIndex,
		i: index,
		p: currentSlide
		// active: getOpacityForIndex
	});

	return (
		<Slider ref={sliderRef}>
			{sliderRef.current &&
				(children.length > 1 ? (
					<>
						<MotionSlider {...dragConfig} onDragEnd={handleDragEnd}>
							{children.map((item, index) => (
								<SlideItemWrapper
									withinSidebar={withinSidebar}
									key={`slide-item-${item.key ?? item.props.id ?? index}`}
									variants={variants}
									initial="next"
									animate={controls}
									style={getItemStyle()}
									transition={itemTransition}
									custom={getCustomProps(index)}
									itemWidth={itemWidth}
								>
									{item}
								</SlideItemWrapper>
							))}
						</MotionSlider>
						{displayArrows && (
							<>
								<ArrowLeft
									withinSidebar={withinSidebar}
									show={currentSlide > 0}
									onClick={onPrevious}
									dataAt="left-arrow-button-bonus-carousel"
									top={arrowsTopPosition}
									arrowStyle={arrowStyle}
								>
									<Icon className="slider__arrow-icon" type={ICON_LEFT} />
								</ArrowLeft>
								<ArrowRight
									show={currentSlide < children.length - 1}
									onClick={onNext}
									dataAt="right-arrow-button-bonus-carousel"
									top={arrowsTopPosition}
									arrowStyle={arrowStyle}
								>
									<Icon className="slider__arrow-icon" type={ICON_RIGHT} />
								</ArrowRight>
							</>
						)}
					</>
				) : (
					<SingleItemWrapper itemWidth={itemWidth}>{children}</SingleItemWrapper>
				))}
		</Slider>
	);
};

export default WrappedSlider;
